import { useReducer, useEffect } from 'react';

import type { PlotValue } from '../../../../../../types';

import {
  SELLER_ANALYTICS_CATEGORIES,
  SELLER_ANALYTICS_TIME_RANGES,
  SELLER_ANALYTICS_CATEGORY_DISPLAY_TEXT,
  SELLER_ANALYTICS_TIME_RANGE_DISPLAY_TEXT,
} from '../../../../../../types';

export enum ACTION_TYPE {
  SET_SELECTED_CATEGORY = 'SET_SELECTED_CATEGORY',
  SET_DISPLAYED_TIME_RANGE = 'SET_DISPLAYED_TIME_RANGE',
  SET_TOTAL = 'SET_TOTAL',
  SET_SELLER_ANALYTICS_DATA = 'SET_SELLER_ANALYTICS_DATA',
}

type SetSelectedCategoryType = {
  type: ACTION_TYPE.SET_SELECTED_CATEGORY;
  payload: SELLER_ANALYTICS_CATEGORIES;
};

type SetDisplayedTimeRangeType = {
  type: ACTION_TYPE.SET_DISPLAYED_TIME_RANGE;
  payload: SELLER_ANALYTICS_TIME_RANGE_DISPLAY_TEXT;
};

type SetTotalType = {
  type: ACTION_TYPE.SET_TOTAL;
  payload?: number;
};

type SetSellerAnalyticsType = {
  type: ACTION_TYPE.SET_SELLER_ANALYTICS_DATA;
  payload?: PlotValue[];
};

type StateType = {
  selectedCategory: SELLER_ANALYTICS_CATEGORIES;
  displayedTimeRange: SELLER_ANALYTICS_TIME_RANGE_DISPLAY_TEXT;
  total: number;
  sellerAnalyticsData?: PlotValue[];
};

type ActionType =
  | SetSelectedCategoryType
  | SetDisplayedTimeRangeType
  | SetTotalType
  | SetSellerAnalyticsType;

const useSellerAnalyticsModalDisplayReducer = (
  state: StateType,
  action: ActionType,
) => {
  const { type, payload } = action;
  switch (type) {
    case ACTION_TYPE.SET_SELECTED_CATEGORY:
      return {
        ...state,
        selectedCategory: payload as SELLER_ANALYTICS_CATEGORIES,
      };
    case ACTION_TYPE.SET_DISPLAYED_TIME_RANGE:
      return {
        ...state,
        displayedTimeRange: payload as SELLER_ANALYTICS_TIME_RANGE_DISPLAY_TEXT,
      };
    case ACTION_TYPE.SET_TOTAL:
      return {
        ...state,
        total: payload as number,
      };
    case ACTION_TYPE.SET_SELLER_ANALYTICS_DATA:
      return {
        ...state,
        sellerAnalyticsData: payload as PlotValue[],
      };
    default:
      return state;
  }
};

interface IUseSellerAnalyticsModalDisplay {
  selectedCategory: SELLER_ANALYTICS_CATEGORIES;
  displayedTimeRange: SELLER_ANALYTICS_TIME_RANGE_DISPLAY_TEXT;
  sellerAnalyticsData: PlotValue[];
  fetchSellerAnalyticsData: (
    category: SELLER_ANALYTICS_CATEGORIES,
    range: SELLER_ANALYTICS_TIME_RANGES,
  ) => void;
}

interface Output {
  setSelectedCategory: (t: SELLER_ANALYTICS_CATEGORIES) => void;
  setDisplayedTimeRange: (t: SELLER_ANALYTICS_TIME_RANGE_DISPLAY_TEXT) => void;
  setTotal: (total: number) => void;
  getTotal: (total?: PlotValue[]) => number;
  handleChange: (t: string) => void;
  mapCategoryValuesToDisplayText: (
    t: SELLER_ANALYTICS_CATEGORIES,
  ) => SELLER_ANALYTICS_CATEGORY_DISPLAY_TEXT;
  selectedCategory: SELLER_ANALYTICS_CATEGORIES;
  displayedTimeRange: SELLER_ANALYTICS_TIME_RANGE_DISPLAY_TEXT;
  total: number;
  sellerAnalyticsData?: PlotValue[];
  mapTimeRangeDisplayTextToValue: (
    timeRange: SELLER_ANALYTICS_TIME_RANGE_DISPLAY_TEXT,
  ) => SELLER_ANALYTICS_TIME_RANGES;
}

const useSellerAnalyticsModalDisplay: (
  t: IUseSellerAnalyticsModalDisplay,
) => Output = ({
  selectedCategory,
  displayedTimeRange,
  sellerAnalyticsData,
  fetchSellerAnalyticsData,
}) => {
  const [state, dispatch] = useReducer(useSellerAnalyticsModalDisplayReducer, {
    selectedCategory,
    displayedTimeRange,
    total: 0,
    sellerAnalyticsData,
  });

  const setSelectedCategory = (category: SELLER_ANALYTICS_CATEGORIES) =>
    dispatch({
      type: ACTION_TYPE.SET_SELECTED_CATEGORY,
      payload: category,
    });

  const setDisplayedTimeRange = (
    timeRange: SELLER_ANALYTICS_TIME_RANGE_DISPLAY_TEXT,
  ) =>
    dispatch({
      type: ACTION_TYPE.SET_DISPLAYED_TIME_RANGE,
      payload: timeRange,
    });

  const setTotal = (t?: number) =>
    dispatch({
      type: ACTION_TYPE.SET_TOTAL,
      payload: t,
    });

  function mapTimeRangeDisplayTextToValue(
    timeRange: SELLER_ANALYTICS_TIME_RANGE_DISPLAY_TEXT,
  ) {
    switch (timeRange) {
      case SELLER_ANALYTICS_TIME_RANGE_DISPLAY_TEXT.LAST_7_DAYS:
        return SELLER_ANALYTICS_TIME_RANGES.SEVEN_DAYS;
      case SELLER_ANALYTICS_TIME_RANGE_DISPLAY_TEXT.LAST_30_DAYS:
        return SELLER_ANALYTICS_TIME_RANGES.THIRTY_DAYS;
      case SELLER_ANALYTICS_TIME_RANGE_DISPLAY_TEXT.LAST_90_DAYS:
        return SELLER_ANALYTICS_TIME_RANGES.NINETY_DAYS;
      default:
        return SELLER_ANALYTICS_TIME_RANGES.SEVEN_DAYS;
    }
  }

  function mapCategoryValuesToDisplayText(
    category: SELLER_ANALYTICS_CATEGORIES,
  ) {
    switch (category) {
      case SELLER_ANALYTICS_CATEGORIES.AD_VIEWS:
        return SELLER_ANALYTICS_CATEGORY_DISPLAY_TEXT.VIEWS;
      case SELLER_ANALYTICS_CATEGORIES.SAVES:
        return SELLER_ANALYTICS_CATEGORY_DISPLAY_TEXT.SAVES;
      case SELLER_ANALYTICS_CATEGORIES.LEADS:
        return SELLER_ANALYTICS_CATEGORY_DISPLAY_TEXT.LEADS;
      default:
        return SELLER_ANALYTICS_CATEGORY_DISPLAY_TEXT.VIEWS;
    }
  }

  async function handleChange(category: SELLER_ANALYTICS_CATEGORIES) {
    setSelectedCategory(category);
    fetchSellerAnalyticsData(
      category,
      mapTimeRangeDisplayTextToValue(state.displayedTimeRange),
    );
  }

  function getTotal(t?: PlotValue[]) {
    if (t) {
      return t.reduce((acc, y: PlotValue) => acc + y.value, 0);
    } else return 0;
  }

  useEffect(() => {
    setTotal(getTotal(sellerAnalyticsData));
  }, [sellerAnalyticsData]);

  return {
    ...state,
    setSelectedCategory,
    setDisplayedTimeRange,
    setTotal,
    getTotal,
    handleChange,
    mapCategoryValuesToDisplayText,
    mapTimeRangeDisplayTextToValue,
  };
};

export { useSellerAnalyticsModalDisplay };
